/**
 * Unified SSE Client — multiplexes all indicator SSE streams into a single connection.
 *
 * Solves the browser's 6-connection-per-origin limit (HTTP/1.1) by combining
 * all SSE channels into one EventSource to /sse/unified.
 *
 * Usage:
 *   import { unifiedSSE } from '../hooks/useUnifiedSSE';
 *
 *   // Subscribe (returns unsubscribe function)
 *   const unsub = unifiedSSE.subscribe('vwap', 'vwap_snapshot', handleSnapshot);
 *   const unsub2 = unifiedSSE.subscribe('vwap', 'vwap_update', handleUpdate);
 *
 *   // Unsubscribe
 *   unsub();
 *   unsub2();
 */

import CONFIG from '../config';
import { getStreamUrl } from '../auth/authFetch';

class UnifiedSSEClient {
  constructor() {
    /** @type {EventSource|null} */
    this._es = null;
    /** @type {Map<string, Map<string, Set<Function>>>} channel -> event -> handlers */
    this._handlers = new Map();
    /** @type {Set<string>} Active channel names (INTENT — what we want subscribed) */
    this._channels = new Set();
    /** @type {Set<string>} Channels the CURRENT open EventSource was actually opened with (REALITY).
     *  The server only forwards channels named in the connection URL, so `_channels` alone (intent)
     *  is not a safe proxy for "the server is forwarding this channel." */
    this._connectedChannels = new Set();
    /** @type {number|null} Debounce timer for reconnections */
    this._reconnectTimer = null;
    /** @type {number} Hard deadline (ms epoch) by which a pending reconnect MUST fire, so a burst
     *  of subscribe/unsubscribe can't starve it indefinitely by resetting the debounce. */
    this._reconnectDeadline = 0;
    /** @type {boolean} */
    this._connected = false;
    /** @type {number} Reconnect error count */
    this._errorCount = 0;
  }

  /**
   * Subscribe to events on a channel.
   *
   * @param {string} channel - SSE channel name (e.g., 'es:5m', 'vwap', 'greeks:5m')
   * @param {string} eventName - Original event name (e.g., 'candle', 'vwap_snapshot')
   * @param {Function} handler - Callback receiving parsed JSON data
   * @returns {Function} Unsubscribe function
   */
  subscribe(channel, eventName, handler) {
    if (!this._handlers.has(channel)) {
      this._handlers.set(channel, new Map());
    }
    const channelHandlers = this._handlers.get(channel);
    if (!channelHandlers.has(eventName)) {
      channelHandlers.set(eventName, new Set());
    }
    channelHandlers.get(eventName).add(handler);

    // Track channel and reconnect if needed
    this._channels.add(channel);
    // Decide on REALITY (what the open connection actually carries), not INTENT (`_channels`).
    // A channel can be in `_channels` yet absent from the live socket's URL — e.g. it was added
    // during a pending debounce, or a prior page left it in `_channels` while the current socket
    // was opened without it. In those cases `_registerEvents()` would attach a listener the server
    // never feeds. Only take the cheap register-listener path when the channel is genuinely on the
    // wire; otherwise force a reconnect so the server starts forwarding it.
    if (!this._es || !this._connectedChannels.has(channel)) {
      // The live connection does not carry this channel — (re)connect so the server forwards it.
      this._scheduleReconnect();
    } else if (this._registerEvents) {
      // New EVENT TYPE on a channel the live connection ALREADY carries (e.g. a second component
      // subscribing 'renko_rotations' on es:renko:8.0 that another already opened for
      // 'renko_brick'/'renko_forming'). The server already forwards EVERY event type on a
      // subscribed channel, so no reconnect is needed -- but the browser EventSource needs an
      // addEventListener for this {channel}:{eventName} or it SILENTLY DROPS the event until the
      // next full reconnect. registerEvents() is idempotent (guarded by its per-connection
      // registeredEvents Set), so it only ever adds what's missing.
      this._registerEvents();
    }

    // Return unsubscribe function
    return () => {
      this.unsubscribe(channel, eventName, handler);
    };
  }

  /**
   * Unsubscribe a handler from a channel event.
   */
  unsubscribe(channel, eventName, handler) {
    const channelHandlers = this._handlers.get(channel);
    if (!channelHandlers) return;

    const handlers = channelHandlers.get(eventName);
    if (handlers) {
      handlers.delete(handler);
      if (handlers.size === 0) {
        channelHandlers.delete(eventName);
      }
    }

    // If no more handlers for this channel, remove it
    if (channelHandlers.size === 0) {
      this._handlers.delete(channel);
      this._channels.delete(channel);
      this._scheduleReconnect();
    }
  }

  /**
   * Debounced reconnection — batches rapid subscribe/unsubscribe calls
   * into a single reconnect (e.g., when toggling multiple indicators).
   */
  _scheduleReconnect() {
    const now = Date.now();
    // On the first schedule of a pending burst, set a hard deadline; subsequent calls keep debouncing
    // but can never push the actual reconnect past it, so continuous subscribe/unsubscribe churn
    // can't starve a newly-added channel out of the live connection.
    if (!this._reconnectDeadline) {
      this._reconnectDeadline = now + 300;
    }
    if (this._reconnectTimer) {
      clearTimeout(this._reconnectTimer);
      this._reconnectTimer = null;
    }
    if (now >= this._reconnectDeadline) {
      this._connect();
      return;
    }
    const delay = Math.min(100, this._reconnectDeadline - now);
    this._reconnectTimer = setTimeout(() => {
      this._reconnectTimer = null;
      this._connect();
    }, delay);
  }

  /**
   * Reconnect after a connection error with exponential backoff. Each attempt
   * goes through _connect, which fetches a FRESH stream ticket — the browser's
   * native EventSource retry would reuse the old URL's expired ticket.
   */
  _scheduleErrorReconnect() {
    if (this._reconnectTimer) return;  // a reconnect is already pending
    const delay = Math.min(30000, 1000 * 2 ** Math.min(Math.max(this._errorCount - 1, 0), 5));
    this._reconnectTimer = setTimeout(() => {
      this._reconnectTimer = null;
      this._connect();
    }, delay);
  }

  /**
   * Connect (or reconnect) to the unified SSE endpoint with current channels.
   */
  async _connect() {
    // A reconnect is now firing — clear the pending debounce/deadline for the next burst.
    this._reconnectDeadline = 0;
    if (this._reconnectTimer) {
      clearTimeout(this._reconnectTimer);
      this._reconnectTimer = null;
    }

    // Close existing connection
    if (this._es) {
      this._es.close();
      this._es = null;
      this._connected = false;
    }

    // No channels — stay disconnected
    if (this._channels.size === 0) {
      this._connectedChannels = new Set();
      console.log('[UnifiedSSE] No channels — disconnected');
      return;
    }

    // Snapshot the channel set this socket is being opened with (REALITY). subscribe() compares
    // against this to decide reconnect-vs-register-listener, and onopen reconciles against `_channels`.
    const openingChannels = new Set(this._channels);
    this._connectedChannels = openingChannels;
    const channelList = Array.from(openingChannels).join(',');
    const url = `${CONFIG.apiUrl}/sse/unified?channels=${encodeURIComponent(channelList)}`;
    console.log('[UnifiedSSE] Connecting:', channelList);

    let streamUrl;
    try {
      streamUrl = await getStreamUrl(url);
    } catch (err) {
      // Ticket fetch failed (network blip, or 401 — authFetch already cleared
      // the session and is reloading the page). Retry with backoff.
      console.warn('[UnifiedSSE] Stream ticket fetch failed:', err.message);
      this._errorCount++;
      this._scheduleErrorReconnect();
      return;
    }

    // A newer _connect re-snapshotted while the ticket was in flight — it owns the socket now.
    if (this._connectedChannels !== openingChannels) return;

    try {
      const es = new EventSource(streamUrl);
      this._es = es;

      es.onopen = () => {
        console.log('[UnifiedSSE] Connected');
        this._connected = true;
        this._errorCount = 0;
        // Reconcile intent vs reality: if a channel was added to `_channels` while this socket was
        // opening, it isn't in the URL the server is forwarding — reconnect to pick it up. Bounded:
        // it only re-fires while the two sets diverge, and each _connect re-snapshots them.
        for (const ch of this._channels) {
          if (!openingChannels.has(ch)) {
            console.log('[UnifiedSSE] Channel added during connect — reconciling:', ch);
            this._scheduleReconnect();
            break;
          }
        }
      };

      es.onerror = () => {
        this._connected = false;
        this._errorCount++;
        // Don't let the browser's native retry reuse this URL: its ticket is
        // single-window; reconnect through _connect for a fresh one.
        es.close();
        if (this._es === es) {
          console.warn('[UnifiedSSE] Connection error — reconnecting with fresh ticket');
          this._scheduleErrorReconnect();
        }
      };

      // Listen for all events — EventSource fires 'message' for unnamed events,
      // but our server sends named events like "es:5m:candle".
      // We need to register listeners for each possible prefixed event.
      // Instead, use the onmessage fallback + a custom approach:
      // We override addEventListener dynamically based on known event patterns.
      // Actually, the cleanest approach is to listen on the raw EventSource.
      // Since we can't predict all event names, we use a MutationObserver-like pattern.
      // The solution: patch the EventSource to capture all events.
      this._setupEventRouting(es);

    } catch (err) {
      console.error('[UnifiedSSE] Connection error:', err);
    }
  }

  /**
   * Route incoming SSE events to the correct handlers.
   *
   * SSE events arrive with event type "{channel}:{original_event}" (e.g., "es:5m:candle").
   * We register listeners for each active channel:event combination.
   */
  _setupEventRouting(es) {
    // Build a set of all event types we need to listen for
    // Format: "{channel}:{eventName}"
    const registeredEvents = new Set();

    const registerEvents = () => {
      // Remove old listeners that are no longer needed
      // (EventSource doesn't have removeEventListener that works well,
      //  so we just track what's registered and only add new ones)

      for (const [channel, eventMap] of this._handlers) {
        for (const eventName of eventMap.keys()) {
          const fullEventName = `${channel}:${eventName}`;
          if (!registeredEvents.has(fullEventName)) {
            registeredEvents.add(fullEventName);
            console.debug('[UnifiedSSE] Registering listener:', fullEventName);  // TEMP diagnostic
            es.addEventListener(fullEventName, (event) => {
              this._dispatchEvent(channel, eventName, event);
            });
          }
        }
      }
    };

    // Register all current events
    registerEvents();

    // Store the register function so we can call it when new subscriptions arrive
    this._registerEvents = registerEvents;
  }

  /**
   * Dispatch a received SSE event to registered handlers.
   */
  _dispatchEvent(channel, eventName, event) {
    const channelHandlers = this._handlers.get(channel);
    if (!channelHandlers) return;

    const handlers = channelHandlers.get(eventName);
    if (!handlers || handlers.size === 0) return;

    try {
      const data = JSON.parse(event.data);
      for (const handler of handlers) {
        try {
          handler(data);
        } catch (err) {
          console.error(`[UnifiedSSE] Handler error for ${channel}:${eventName}:`, err);
        }
      }
    } catch (err) {
      console.error(`[UnifiedSSE] Parse error for ${channel}:${eventName}:`, err);
    }
  }

  /**
   * Disconnect and clean up.
   */
  disconnect() {
    if (this._reconnectTimer) {
      clearTimeout(this._reconnectTimer);
      this._reconnectTimer = null;
    }
    this._reconnectDeadline = 0;
    if (this._es) {
      this._es.close();
      this._es = null;
    }
    this._connected = false;
    this._handlers.clear();
    this._channels.clear();
    this._connectedChannels.clear();
  }

  /** @returns {boolean} Whether the client is connected */
  get connected() {
    return this._connected;
  }

  /** @returns {number} Number of active channels */
  get channelCount() {
    return this._channels.size;
  }
}

// Singleton instance — shared across all indicators
export const unifiedSSE = new UnifiedSSEClient();

export default unifiedSSE;
